/**
 * Frontend subscription-state helpers.
 *
 * Mirrors the canonical precedence implemented in
 * backend/subscriptions/state.py and
 * backend/firebase/functions/mailerliteSync/user-state.js
 * (docs/complimentary-access-expiration.md §3.3). CRA prevents sharing a
 * single file across environments, so any change here must be mirrored there.
 */

// Stable discriminator written by the complimentary-expiry migration.
export const COMPLIMENTARY_END_REASON = 'complimentary_access_expired';

const FULL_ACCESS_STRIPE_STATUSES = ['trialing', 'active'];

/**
 * True when the user document carries a valid complimentary-ended marker:
 * active grant off, end timestamp present, and the expected end reason.
 * An active isGrandfathered flag always wins (e.g. after rollback).
 */
export function hasComplimentaryExpiredMarker(userData) {
  if (!userData || typeof userData !== 'object') return false;
  if (userData.isGrandfathered === true) return false;
  if (!userData.grandfatheredEndedAt) return false;
  return userData.grandfatheredEndReason === COMPLIMENTARY_END_REASON;
}

/**
 * Canonical user-type resolution for routing and copy.
 *
 * @param {Object} params
 * @param {boolean} params.isGrandfathered - active complimentary grant
 * @param {Object|null} params.subscriptionStatus - { status } from AuthContext
 * @param {boolean} params.hasSubscriptionHistory - any Stripe subscription doc exists
 * @param {boolean} [params.historyKnown=true] - false when the history read failed
 * @param {boolean} [params.complimentaryExpired=false] - valid end marker present
 * @returns {'grandfathered'|'active'|'lapsed'|'complimentary_expired'|'never_subscribed'|'unknown'}
 *
 * 'unknown' means a required read failed: callers must stay on a defensive
 * path (retry / keep current access) and never route to registration off it.
 */
/**
 * Reason surfaced to upgrade/locked-feature UX when access is missing.
 *
 * @returns {'none'|'trial_expired'|'complimentary_expired'|'no_subscription'|'subscription_inactive'|'unknown'}
 *
 * 'unknown' means the history read failed: callers must show retry-oriented
 * copy, never registration ("no_subscription") or payment messaging — the
 * same no-downgrade rule deriveUserType follows.
 */
export function deriveUpgradeReason({
  isGrandfathered,
  subscriptionStatus,
  hasSubscriptionHistory,
  historyKnown = true,
  complimentaryExpired = false,
}) {
  if (isGrandfathered) return 'none'; // grandfathered users don't need to upgrade

  if (subscriptionStatus?.status === 'trialing') return 'trial_expired';

  // The remaining reasons all depend on the history/marker reads.
  if (!historyKnown) return 'unknown';

  if (!subscriptionStatus || subscriptionStatus.status === 'none') {
    if (hasSubscriptionHistory) return 'trial_expired';
    // Distinct reason so callers never show trial/payment copy to users
    // whose complimentary access ended.
    if (complimentaryExpired) return 'complimentary_expired';
    return 'no_subscription';
  }

  return 'subscription_inactive';
}

export function deriveUserType({
  isGrandfathered,
  subscriptionStatus,
  hasSubscriptionHistory,
  historyKnown = true,
  complimentaryExpired = false,
}) {
  if (isGrandfathered) return 'grandfathered';

  if (subscriptionStatus && FULL_ACCESS_STRIPE_STATUSES.includes(subscriptionStatus.status)) {
    return 'active';
  }

  // Without history we cannot distinguish lapsed from never_subscribed (or
  // complimentary_expired), and never_subscribed redirects into registration —
  // so a failed read must resolve to 'unknown', never a downgrade.
  if (!historyKnown) return 'unknown';

  if (hasSubscriptionHistory) return 'lapsed';
  if (complimentaryExpired) return 'complimentary_expired';
  return 'never_subscribed';
}
